import {
  IOrganizationChartExternalUser,
  IOrganizationChartNode,
  NodeOptionNode,
  NodeOptionUser,
  NodePosition,
  ParentRef,
  PositionLikeNode,
} from './node.types';

export const NODE_WIDTH = 200;
export const NODE_HEIGHT = 290;
export const H_GAP = 50;
export const V_GAP = 80;
export const ROOT_Y = 0;

function tryParseNodePosition(node: PositionLikeNode): NodePosition | null {
  const x = node.positionX;
  const y = node.positionY;
  if (x === undefined || x === null || y === undefined || y === null) {
    return null;
  }
  if (String(x).trim() === '' || String(y).trim() === '') {
    return null;
  }

  const parsedX = Number(x);
  const parsedY = Number(y);
  if (!Number.isFinite(parsedX) || !Number.isFinite(parsedY)) {
    return null;
  }
  return { x: parsedX, y: parsedY };
}

/** Persisted flow coordinates for a chart node (required after API normalization). */
export function getNodePosition(node: PositionLikeNode): NodePosition {
  const parsed = tryParseNodePosition(node);
  if (!parsed) {
    const id = node.id !== undefined && node.id !== null ? String(node.id) : '?';
    throw new Error(`Chart node ${id} is missing valid positionX/positionY`);
  }
  return parsed;
}

export function rightOfRightmost(positions: NodePosition[]): NodePosition {
  const rightmost = positions.reduce((max, current) => (current.x > max.x ? current : max), positions[0]);
  return { x: rightmost.x + NODE_WIDTH + H_GAP, y: rightmost.y };
}

/** Y coordinate for a row placed directly below a parent node. */
export function yBelowParent(parentPos: NodePosition): number {
  return parentPos.y + NODE_HEIGHT + V_GAP;
}

/** Horizontal center of a parent node (top-left position + half width). */
export function parentCenterX(parentPos: NodePosition): number {
  return parentPos.x + NODE_WIDTH / 2;
}

/** All nodes that are descendants of `rootId` (children, grandchildren, …) per parentIds edges. */
export function descendantIdsOfNode(
  rootId: IOrganizationChartNode['id'],
  nodeList: IOrganizationChartNode[],
): Set<string> {
  const byParentId = new Map<string, IOrganizationChartNode[]>();
  for (const n of nodeList) {
    for (const pid of n.parentIds ?? []) {
      const key = String(pid);
      const list = byParentId.get(key) ?? [];
      list.push(n);
      byParentId.set(key, list);
    }
  }
  const out = new Set<string>();
  const queue = [String(rootId)];
  while (queue.length) {
    const id = queue.shift();
    if (id === undefined) {
      break;
    }
    for (const child of byParentId.get(id) ?? []) {
      const cid = String(child.id);
      if (!out.has(cid)) {
        out.add(cid);
        queue.push(cid);
      }
    }
  }
  return out;
}

/** Non-empty display name from embedded external user, if any. */
export function chartNodeExternalUserDisplayName(
  externalUser: IOrganizationChartExternalUser | null | undefined,
): string {
  if (!externalUser) {
    return '';
  }
  return `${externalUser.firstName ?? ''} ${externalUser.lastName ?? ''}`.trim();
}

/**
 * Builds node select options and disambiguates duplicate seat names by appending person full name.
 * Example: "Manager - Jane Doe" when multiple nodes share title "Manager".
 */
export function chartNodeOptionsWithDisambiguatedTitles(
  nodes: NodeOptionNode[],
  usersById?: Map<string, NodeOptionUser>,
): Array<{ value: IOrganizationChartNode['id']; label: string }> {
  const titleOccurrences = new Map<string, number>();
  nodes.forEach((node) => {
    const titleKey = node.nodeTitle.trim().toLowerCase();
    titleOccurrences.set(titleKey, (titleOccurrences.get(titleKey) || 0) + 1);
  });

  return nodes.map((node) => {
    const title = node.nodeTitle.trim();
    const titleKey = title.toLowerCase();
    const hasDuplicateTitle = (titleOccurrences.get(titleKey) || 0) > 1;

    if (!hasDuplicateTitle) {
      return { value: node.id, label: title };
    }

    const externalUserName = chartNodeExternalUserDisplayName(node.externalUser);
    const tenantUser = node.userId ? usersById?.get(String(node.userId)) : undefined;
    const tenantUserName = tenantUser ? `${tenantUser.firstName ?? ''} ${tenantUser.lastName ?? ''}`.trim() : '';
    const fullName = externalUserName || tenantUserName;

    return {
      value: node.id,
      label: fullName ? `${title} - ${fullName}` : title,
    };
  });
}

/** First parent for tree layout and legacy UI; undefined if root. */
export function chartNodePrimaryParentId(node: ParentRef): string | undefined {
  if (node.parentIds?.length) {
    return node.parentIds[0];
  }
  if (Array.isArray(node.parentId)) {
    return node.parentId[0] ? String(node.parentId[0]) : undefined;
  }
  return node.parentId ? String(node.parentId) : undefined;
}

/** All parent ids for edges (multi-parent). */
export function chartNodeParentIdList(node: ParentRef): string[] {
  if (node.parentIds?.length) {
    return [...node.parentIds];
  }
  if (Array.isArray(node.parentId)) {
    return node.parentId.map(String);
  }
  return node.parentId ? [String(node.parentId)] : [];
}
